<script>import { createEventDispatcher } from "svelte";
import { fly } from "svelte/transition";
import Resizer from "../../v6/Resizer/Resizer.svelte";
import Tooltip from "../Tooltip/Tooltip.svelte";
const dispatch = createEventDispatcher();
export let reactions = [];
export let minimode = 1130;
export let showResults = true;
export let reactionClicked = false;
export let clickedReactionId = null;
export let resizer = true;
export let showTooltip = false;
export let tooltip = {};
let reactionContainerHeight = 55;
let mobile = true;
let innerWidth = window.innerWidth;
let tooltipHeight;
$: {
  const likedReaction = reactions.find((reaction) => reaction.liked);
  if (likedReaction) {
    clickedReactionId = likedReaction.id;
    reactionClicked = true;
  }
}
$:
  totalCount = reactions.reduce((sum, reaction) => sum + reaction.count, 0);
$:
  reactionResults = calculateNormalizedPercentages(reactions, totalCount);
$:
  mobile = innerWidth < minimode;
const handleReactionClick = (id) => {
  reactionClicked = true;
  if (clickedReactionId !== id) {
    reactions = reactions.map((reaction) => {
      if (reaction.id === id) {
        return {
          ...reaction,
          count: reaction.liked ? reaction.count - 1 : reaction.count + 1,
          liked: !reaction.liked
        };
      } else if (reaction.liked) {
        return {
          ...reaction,
          count: reaction.count - 1,
          liked: false
        };
      }
      return reaction;
    });
    clickedReactionId = id;
  }
  dispatch("clickedReactionId", id);
};
const calculatePercentage = (count, totalCount2) => {
  const percentage = totalCount2 > 0 ? Math.round(count / totalCount2 * 100) : count > 0 ? 100 : 0;
  return percentage > 100 ? 100 : percentage;
};
const calculatePercentageDependOnScreenSize = (percentage, mobile2) => {
  return mobile2 ? reactionContainerHeight * percentage / 100 : percentage;
};
const calculateNormalizedPercentages = (reactions2, totalCount2) => {
  if (!reactions2 || reactions2.length === 0) {
    return [];
  }
  const percentages = reactions2.map(
    ({ count }) => calculatePercentage(count, totalCount2)
  );
  const percentageSum = percentages.reduce(
    (total, percentage) => total + percentage,
    0
  );
  if (percentageSum > 100) {
    const percentageDiff = percentageSum - 100;
    const largestPercentage = percentages.reduce(
      (largest, current, index) => largest.value < current ? { value: current, index } : largest,
      { value: 0, index: -1 }
    );
    if (largestPercentage.index !== -1) {
      percentages[largestPercentage.index] -= percentageDiff;
    }
  } else if (percentageSum < 100) {
    const percentageDiff = 100 - percentageSum;
    const smallestPercentage = percentages.reduce(
      (smallest, current, index) => smallest.value > current ? { value: current, index } : smallest,
      { value: 100, index: -1 }
    );
    if (smallestPercentage.index !== -1) {
      percentages[smallestPercentage.index] += percentageDiff;
    }
  }
  return percentages;
};
</script>

<style>.reactions-container {
  display: flex;
  flex-direction: row;
  gap: 0;
  max-width: 250px;
  justify-content: space-between;
  align-items: flex-end;
  padding: 0 10px;
  padding-bottom: 18px;
  max-height: 136px;
}

.reaction-container {
  position: relative;
  width: 48px;
}

.tooltip {
  position: absolute;
  width: 170px;
  top: var(--tooltip-top);
  left: 10px;
  transition: all 0.3s ease-in-out;
  z-index: -1;
  pointer-events: none;
  opacity: 0;
}

.reaction-container:hover .tooltip {
  z-index: 2;
  pointer-events: auto;
  opacity: 1;
}

.container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.reaction {
  width: 32px;
  aspect-ratio: 1;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
  filter: drop-shadow(0 6px 6px rgba(0, 0, 0, 0.25));
  transition: scale 0.1s ease-in-out;
  background-image: var(--reaction-image);
}

@media only screen and (resolution >= 192dpi) {
  .reaction {
    background-image: var(--reaction-image2x);
  }
}

.reaction:hover {
  cursor: pointer;
}

.reaction.active {
  animation: bounce 1.2s ease;
}

@keyframes bounce {
  70% {
    transform: translateY(0%);
  }
  80% {
    transform: translateY(-30%);
  }
  90% {
    transform: translateY(0%);
  }
  95% {
    transform: translateY(-15%);
  }
  97% {
    transform: translateY(0%);
  }
  99% {
    transform: translateY(-5%);
  }
  100% {
    transform: translateY(0);
  }
}

.bar-chart {
  width: calc(100% - 2px);
  display: flex;
  justify-content: center;
  position: absolute;
  bottom: 0;
  z-index: -1;
  transition: all 1s ease-in-out;
}

.bar-chart.active {
  background: linear-gradient(180deg, hsl(var(--color-N32)) 0%, hsl(var(--color-N20)) 107.5%);
}

.bar-chart.checked {
  background: linear-gradient(180deg, hsl(var(--color-N20)) 0%, hsl(var(--color-N32)) 40.52%, hsl(var(--color-accent)) 151.48%);
}

.percent-container {
  height: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  transition: all 1s ease-in-out;
  min-height: 0;
}

.percent-container.active, .percent-container.checked {
  min-height: 19px;
}

.percent-container.active::before, .percent-container.checked::before {
  content: "";
  position: absolute;
  width: 100%;
  height: 1px;
  bottom: 0;
  background-color: hsl(var(--color-N32));
}

.percent-container.checked::before {
  background-color: hsl(var(--color-accent));
}

.percent {
  color: var(--theme-text-color);
  text-align: center;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Averta, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 14px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  position: relative;
  top: 10%;
}

.divider {
  width: 32px;
  aspect-ratio: 1;
  height: 32px;
  position: relative;
}

.divider::before {
  content: "";
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 2px;
  height: 100%;
  background: hsl(var(--color-N20));
  transform: rotate(45deg);
  border-radius: 10px;
}</style>

<svelte:window bind:innerWidth />

<div class="reactions-container">
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  {#each reactions as { emoticon, id, name }, index (id)}
    <div
      class="reaction-container"
      id="reaction-{id}"
      on:click={() => handleReactionClick(id)}>
      {#if showTooltip && tooltip?.text}
        <div
          class="tooltip"
          bind:clientHeight={tooltipHeight}
          style:--tooltip-top={tooltipHeight ? `${-tooltipHeight}px` : "-30px"}>
          <Tooltip
            text={showResults ? name : tooltip.text}
            buttonText={showResults ? null : tooltip.buttonText}
            maxWidth={170} />
        </div>
      {/if}
      <div class="container">
        {#if resizer}
          <Resizer w={42} h={42} src={emoticon} let:at1x let:at2x>
            <div
              class="reaction"
              class:active={reactionClicked && showResults}
              style:--reaction-image={`url(${at1x})`}
              style:--reaction-image2x={`url(${at2x})`}>
            </div>
          </Resizer>
        {:else}
          <div
            class="reaction"
            class:active={reactionClicked && showResults}
            style:--reaction-image={`url(${emoticon})`}
            style:--reaction-image2x={`url(${emoticon})`}>
          </div>
        {/if}
        <div
          class="percent-container"
          class:checked={clickedReactionId === id && showResults}
          class:active={reactionClicked && showResults}
          style="height: {reactionClicked && showResults && !mobile
            ? `${reactionResults[index]}px`
            : '0'};">
          {#if reactionClicked && showResults}
            <span transition:fly={{ y: 20, delay: 600, duration: 300 }} class="percent"
              >{reactionResults[index]}%</span>
          {/if}
          <div
            class="bar-chart"
            class:active={reactionClicked && showResults}
            class:checked={clickedReactionId === id && showResults}
            style="height: {reactionClicked && showResults
              ? `${calculatePercentageDependOnScreenSize(reactionResults[index], mobile)}px`
              : '0'};">
          </div>
        </div>
      </div>
    </div>
    {#if index < reactions.length - 1}
      <div class="divider"></div>
    {/if}
  {/each}
</div>
